import {
	Link,
	ListCard,
	Typography,
	useModal,
} from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Source } from "../../../../../schema.dev-sdk-api-appsync.generated";
import { logger } from "../../../../shared/logger";
import { useApiClient } from "../../shared/use-api-client";
import { getOverrideStateDescription } from "../page-terminal";
import { getBadgeProperties } from "../page-terminal/get-badge-properties";
import {
	getSourceOverrideState,
	type OverrideState,
} from "../page-terminal/get-source-override-state";
import { getTerminals } from "../page-terminal/get-terminals";

type SiteTerminal = {
	id: string;
	name: string;
	subtitle: string;
	override: OverrideState;
};

/**
 * The other terminals at the paired terminal's site, pairable with the same
 * rules as Choose Terminal. Renders nothing while empty or on error.
 */
export function AvailableTerminals({
	deviceId,
	terminalId,
	terminalName,
	disabled,
	onPair,
}: Readonly<{
	deviceId: string;
	terminalId: string;
	terminalName: string;
	disabled: boolean;
	onPair: (terminalId: string) => Promise<unknown>;
}>) {
	const terminals = useSiteTerminals({ deviceId, terminalId });
	const [pairing, setPairing] = useState<string>();

	const [cannotPair, setCannotPair] =
		useState<Exclude<OverrideState, "OVERRIDABLE">>();
	const cannotPairModal = useModal({
		variant: "decision",
		title: "Cannot override current pairing",
		content: cannotPair ? getOverrideStateDescription(cannotPair) : undefined,
		secondary: false,
		primary: { label: "Close" },
		overlayBlur: true,
	});

	const [target, setTarget] = useState<SiteTerminal>();
	const overrideModal = useModal({
		variant: "decision",
		title: "Override Pairing",
		content: target && (
			<>
				Are you sure you want to pair the Zeller Terminal{" "}
				<strong>{target.name}</strong> to this device? This will unpair you from{" "}
				<strong>{terminalName}</strong>.
			</>
		),
		primary: {
			label: "Confirm",
			onClick: async () => {
				if (!target) return;
				setPairing(target.id);
				await onPair(target.id);
				setPairing(undefined);
			},
		},
		overlayBlur: true,
	});

	if (terminals instanceof Error || terminals?.length === 0) {
		return null;
	}

	return (
		<section className="flex flex-col gap-12">
			{cannotPairModal.element}
			{overrideModal.element}
			<Typography as="h2" variant="body-base">
				Available
			</Typography>
			{terminals === undefined ? (
				<ListCard.Skeleton avatar subtitle right={{ text: true }} border />
			) : (
				terminals.map((terminal) => (
					<div
						key={terminal.id}
						className={twJoin(
							"flex flex-row items-center gap-12 rounded-8 border p-16",
							"border-grey-90 dark:border-grey-800",
						)}
					>
						<Icon.Terminal
							width={16}
							height={16}
							className="text-blue-1000 dark:text-blue-400"
						/>
						<div className="flex flex-1 flex-col">
							<span className="text-sm">{terminal.name}</span>
							{terminal.subtitle && (
								<span className="text-xs text-grey-550 dark:text-grey-400">
									{terminal.subtitle}
								</span>
							)}
						</div>
						<Link
							label="Pair"
							$button={{ "aria-label": `Pair ${terminal.name}` }}
							pending={pairing === terminal.id}
							disabled={disabled || !!pairing}
							onClick={() => {
								if (terminal.override !== "OVERRIDABLE") {
									setCannotPair(terminal.override);
									return void cannotPairModal.open();
								}
								setTarget(terminal);
								return void overrideModal.open();
							}}
						/>
					</div>
				))
			)}
		</section>
	);
}

function useSiteTerminals({
	deviceId,
	terminalId,
}: {
	deviceId: string;
	terminalId: string;
}) {
	const apiClient = useApiClient();
	const [terminals, setTerminals] = useState<SiteTerminal[] | Error>();

	useEffect(() => {
		let disposed = false;
		setTerminals(undefined);

		void (async () => {
			const response = await getTerminals({ apiClient, deviceId });
			if (disposed) return;
			if (response instanceof Error) {
				logger.error("setup: failed to get site terminals", {
					errorName: response.name,
					errorMessage: response.message,
				});
				return setTerminals(response);
			}

			const sites = response.sites.getSites.sites;
			const site = sites.find((site) =>
				site?.devices?.some((device) => device.id === terminalId),
			);
			if (!site) {
				logger.warn("setup: paired terminal not found in any site", {
					siteCount: sites.length,
					hasMoreSites: !!response.sites.getSites.nextToken,
				});
			}

			setTerminals(
				(site?.devices ?? [])
					.filter((device) => device.id !== terminalId)
					.map(({ id, name, posSettings }) => {
						const source = posSettings?.active ?? undefined;
						return {
							id,
							name: name ?? "Unknown",
							// Unpaired terminals carry no pairing worth naming here.
							subtitle:
								source === Source.STANDALONE
									? ""
									: (getBadgeProperties({
											isPaired: false,
											source,
											sourceName: posSettings?.posRegisterName ?? undefined,
										}).label ?? ""),
							override: getSourceOverrideState(source),
						};
					}),
			);
		})();

		return () => {
			disposed = true;
		};
	}, [apiClient, deviceId, terminalId]);

	return terminals;
}
